import EditorMargin from "@/helpers/cms/EditorMargin.js";
import EditorPadding from "@/helpers/cms/EditorPadding.js";
import CmsBaseClass from "@/helpers/cms/CmsBaseClass.js";
import CmsMediaQuery from "@/helpers/editor/blocks/CmsMediaQuery.js";
import CmsComponent from "@/helpers/editor/components/CmsComponent.js";
import CmsComponentBorder from "@/helpers/editor/components/CmsComponentBorder.js";
import CmsComponentShadow from "@/helpers/editor/components/CmsComponentShadow.js";
import {tr} from "../../../../vue-components/helpers/i18n.js";
import CompatibilityHelper from "@/helpers/CompatibilityHelper.js";

export default class CmsBlock extends CmsBaseClass {
    // to avoid calling constructor from instance
    get typeDef() {
        return this.constructor.typeDef;
    }

    get displayName() {
        if (this.name) {
            return this.name;
        } else if (this.item) {
            return tr(this.item.name);
        } else {
            return tr(this.typeDef.name).toUpperCase();
        }
    }

    // disabled for fixed sections like headers
    get draggable() {
        return true;
    }

    get layoutPresets() {
        return [];
    }

    /**
     * Only for parents
     * @returns {number}
     */
    get maxChildren() {
        return 4;
    }

    /**
     * Limit the type of children that can be appended to the list below
     * Only for parents
     * @returns {*[]}
     */
    get childClasses() {
        return [];
    }

    get parentClasses() {
        return [];
    }

    static typeDef = {
        name: `Block`,
        glyph: `fa-grid-2`,
        type: `block`,
        isParent: false,
        requiresParent: false,
        category: `default`,
        hidden: true
    }

    static BACKGROUNDS = {
        NONE: `none`,
        IMAGE: `image`,
        COLOR: `color`
        // GRADIENT: `gradient`
    }

    static CONTAINERS = {
        CONTAINED: `contained`,
        EXTENDED: `extended`,
        FILL_WIDTH: `fill`
    }

    get ignoredFields() {
        return [`page`, `itemTypeDef`, `item`, `mediaProxy`, `languageProxy`, `templateItem`];
    }

    constructor(data, page) {
        super(data);

        this.type = this.typeDef.type;
        this.padding = new EditorPadding(this.padding);
        this.margin = new EditorMargin(this.margin);
        this.borders = new CmsComponentBorder(this.borders);
        this.shadow = new CmsComponentShadow(this.shadow);

        if (this.mediaQuery && this.mediaQuery.expanded === undefined) {
            this.mediaQuery = null;
        }

        // object to hold mobile, kiosk and tablet variants
        this.mediaQuery = new CmsMediaQuery(this.mediaQuery);

        // The Media Proxy will use the component values or the one from mediaQuery with the device and preview
        this.mediaProxy = this.mediaQuery.createProxy(this, `blockMediaProxy`);

        if (this.gap === undefined) {
            this.gap = {
                x: `10px`,
                y: `10px`
            }
        }

        if (!data) {
            this.name = null;
            this.valid = true;
            this.error = null;
            this.layout = `default`;
            this.theme = `default`;
            this.container = CmsBlock.CONTAINERS.CONTAINED;

            // Todo, reformat as BackgroundComponent
            this.background = CmsBlock.BACKGROUNDS.NONE;
            this.backgroundColor = `#000`;
            this.backgroundImage = null;
            this.backgroundRadius = `0`;

            this.borders.active = false;
            this.shadow.active = false;

            // horizontal alignment
            this.align = `inherit`;

            // any css
            this.css = ``;
            this.customClass = ``;

            this.colorScheme = {};

            this.customId = null;

            // CMS Part
            this.cmsEnabled = false;

            // Todo, reformat as binding {} or component binding
            this.itemId = null;
            this.item = null;
            this.itemType = `inherit`;
            this.pageItemTypeDef = null;

            // for rows/columns
            this.children = [];
            this.childrenReadonly = false;

            if (this.typeDef.isParent) {
                this.enableTemplate = false;
                this.templateItemId = null;
                this.templateItem = null;
            } else {
                this.bypassTemplate = false;
            }
        } else {
            this.backgroundColor = CompatibilityHelper.checkColor(this.backgroundColor);

            if (this.align === `default`) {
                this.align = `inherit`;
            }

            this.children = [];

            for (let child of data.children) {
                this.children.push(window.cmsFactory.new(child, page));
            }
        }
        this.updateTemplateItem();

        if (page) {
            this.setPage(page);
        }
    }

    // suffix is used for lists that reuse the same block for different items
    getId(suffix) {
        if (suffix) {
            return `${(this.customId || this.id)}-${suffix}`;
        } else {
            return (this.customId || this.id);
        }
    }

    getEscapedId(suffix) {
        return CSS.escape(this.getId(suffix));
    }

    hasCustomId() {
        return !isNullOrUndefined(this.customId);
    }

    setPage(page) {
        if (page) {
            this.pageItemTypeDef = page.itemTypeDef;
        }
        this.updateCmsItem();
    }

    // Ensure CMS item is populated from itemId if available
    updateCmsItem() {
        if (!this.itemId)
            return;

        const item = cms.getItemById(this.itemId);
        if (item) {
            this.item = item;
            this.itemType = item.type;
        } else {
            this.item = null;
            this.itemType = null;
        }
    }

    applyChanges(language) {
        for (let key in this) {
            if (this[key] && key !== `mediaProxy` && typeof this[key] === `object`) {
                if (this[key].applyChanges && typeof this[key].applyChanges === `function`) {
                    this[key].applyChanges(language);
                }
            }
        }
    }

    clearObject() {
        for (let key in this) {
            delete this[key];
        }
    }

    mergeBlockIntoJSON(block) {
        const blockJson = block.toJSON();
        let mergeJson = {};
        const ignoredKeys = [`type`];
        for (let key in this) {
            if (ignoredKeys.indexOf(key) === -1 && blockJson[key] !== undefined) {
                mergeJson[key] = blockJson[key];
            }
        }
        return mergeJson;
    }

    duplicate() {
        let copy = new this.constructor(structuredClone(this.toJSON()));
        copy.applyNewId();
        for (let child of copy.children) {
            child.applyNewId();
        }
        return copy;
    }

    /**
     * Assign a new ID to the item and recursively to all it's children
     * Use when duplicating item to ensure children also get new ID
     */
    applyNewId() {
        this.generateId();

        for (let component of this.getComponents(`all`,  true)) {
            component.applyNewId();
        }

        if (this.children) {
            for (let child of this.children) {
                child.applyNewId();
            }
        }
    }

    addChild(childBlock) {
        this.children.push(childBlock);
        this.onChildAdded(childBlock);
        return childBlock;
    }

    checkSelection(selection, blockSelected) {
        if (this.id === blockSelected.id) {
            selection.push(this);
            return true;
        }
        for (let child of this.children) {
            if (child.checkSelection(selection, blockSelected)) {
                selection.push(this);
                return true;
            }
        }
        return false;
    }

    initNewBlock(website) {
        this.colorScheme = website.getColorScheme();
        this.container = website.getDefaultBlockContainer() || CmsBlock.CONTAINERS.CONTAINED
    }

    isChildValid(child) {
        if (this.childClasses.length === 0)
            return true;

        return this.childClasses.some(c => c.typeDef.type === child.type);
    }

    duplicateChild(child) {
        return this.insertChild(child.duplicate(), this.children.indexOf(child));
    }

    insertChild(child, index = 0) {
        if (!this.isChildValid(child))
            return;

        this.children.splice(index + 1, 0, child);
        this.onChildAdded(child);
        return child;
    }

    onChildAdded(child) {
        // to override
    }

    deleteChild(child) {
        this.children.removeItem(child);
    }

    async translate(from, to, replaceAll = false, context = null) {
        const skip = new Set([
            ...this.ignoredFields,
            `children` // we handle children in the second loop
        ]);

        for (let key of Object.keys(this)) {
            if (skip.has(key)) continue; // we skip the ignored fields

            const nested = this[key];

            if (nested != null && typeof nested === `object` && typeof nested.translate === `function`) {
                await nested.translate(from, to, replaceAll, context);
            }
        }

        for (let child of this.children) {
            await child.translate(from, to, replaceAll);
        }
    }

    async resetLanguageTexts(language) {
        const skip = new Set([
            ...this.ignoredFields,
            `children` // we handle children in the second loop
        ]);

        for (let key of Object.keys(this)) {
            if (skip.has(key)) continue; // we skip the ignored fields

            const nested = this[key];

            if (nested != null && typeof nested === `object` && typeof nested.resetLanguageTexts === `function`) {
                await nested.resetLanguageTexts(language);
            }
        }

        for (let child of this.children) {
            await child.resetLanguageTexts(language);
        }
    }

    async swapLanguageTexts(language) {
        const skip = new Set([
            ...this.ignoredFields,
            `children` // we handle children in the second loop
        ]);

        for (let key of Object.keys(this)) {
            if (skip.has(key)) continue; // we skip the ignored fields

            const nested = this[key];

            if (nested != null && typeof nested === `object` && typeof nested.swapLanguageTexts === `function`) {
                await nested.swapLanguageTexts(language);
            }
        }

        for (let child of this.children) {
            await child.swapLanguageTexts(language);
        }
    }

    getComponents(componentType = `all`, includeChildren = true, contentOnly = true) {
        let components = [];

        const skip = new Set([
            ...this.ignoredFields,
            `children` // we handle children in the second loop
        ]);

        for (let key of Object.keys(this)) {
            if (skip.has(key)) continue; // we skip the ignored fields

            const component = this[key];
            if (component && component.active && component instanceof CmsComponent &&
                (componentType === `all` || component.type === componentType)) {
                if (!contentOnly || component.contentFields.length > 0) {
                    components.push(component);
                }
            }
        }

        if (includeChildren) {
            for (let child of this.children) {
                components = components.concat(child.getComponents(componentType, includeChildren, contentOnly));
            }
        }

        return components;
    }

    findComponent(id) {
       const components = this.getComponents();
       return components.find(c => c.id === id);
    }

    setValidation(isValid, error = null) {
        this.valid = isValid;
        this.error = error;
    }

    /**
     * To provide refactoring compatibility only
     * @param oldField {string} old key (ex: height))
     * @param newField {string} new key (ex: size)
     */
    renameField(oldField, newField) {
        if (this.hasOwnProperty(oldField) && !this.hasOwnProperty(newField)) {
            this[newField] = this[oldField];
            delete this[oldField];
            const devices = [`mobile`, `tablet`, `kiosk`];
            for (let device of devices) {
                if (this.mediaQuery[device].hasOwnProperty(oldField) && !this.mediaQuery[device].hasOwnProperty(newField)) {
                    this.mediaQuery[device][newField] = this.mediaQuery[device][oldField];
                    delete this.mediaQuery[device][oldField];
                }
            }
        }
    }

    getClipboardItem(page, parent, copyMode = `copy`) {
        const list = parent ? parent.children : page.blocks;
        const index = list.indexOf(this);

        const copy = this.duplicate();

        return {
            source: `eye-in-cms`,
            type: `block`,
            block: copy.toJSON(),
            parent: parent ? parent.toJSON() : null,
            index,
            mode: copyMode
        };
    }

    applyTemplate() {
        if (!this.enableTemplate || !this.templateItem)
            return;

        const style = this.templateItem.toJSON();

        for (let child of this.children) {
            if (child.id !== this.templateItemId && !child.bypassTemplate && child.type === this.templateItem.type) {
                child.loadStyle(style);
            }
        }
    }

    propagatePadding(parent) {
        if (parent) {
            this.padding.loadStyle(parent.padding);
        }
        for (let child of this.children) {
            child.propagatePadding(this);
        }
    }

    get isHiddenAll() {
        if (!this.mediaQuery) {
            return false;
        }

        const mq = this.mediaQuery;
        const desktop = mq.desktop?.visible ?? true;
        const tablet = mq.tablet?.visible ?? true;
        const mobile = mq.mobile?.visible ?? true;
        const kiosk = mq.kiosk?.visible ?? true;
        return !desktop && !tablet && !mobile && !kiosk;
    }

    toggleHidden() {
        if (!this.mediaQuery) {
            return;
        }
        const mq = this.mediaQuery;
        const makeVisible = this.isHiddenAll;

        const newVal = makeVisible;
        if (mq.desktop) {
            mq.desktop.visible = newVal;
        }
        if (mq.tablet) {
            mq.tablet.visible = newVal;
        }
        if (mq.mobile) {
            mq.mobile.visible = newVal;
        }
        if (mq.kiosk) {
            mq.kiosk.visible = newVal;
        }

        if (typeof this.applyChanges === `function`) {
            this.applyChanges();
        }
    }

    updateTemplateItem() {
        if (!this.enableTemplate || !this.templateItemId) {
            this.templateItem = null;
        }

        this.templateItem = this.children.find(c => c.id === this.templateItemId);
    }

    scrollTo() {
        document.getElementById(this.id)?.scrollIntoView();
    }
}
